import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import {
  deleteChatApi,
  editChatApi,
  getChatHistoryApi,
  markChatAsReadApi,
  deleteInternalChatApi,
  editInternalChatApi,
  getInternalChatHistoryApi,
  markInternalChatAsReadApi,
  getChatGalleryApi,
} from "../../../services/chat.services";
import { getPractitionerSearchApi } from "../../../services/receptionist.services";
import { getUploadUrlApi } from "../../../services/common.services";
import { message, Avatar, theme, Image, Dropdown } from "antd";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { useLocation } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import {
  ACCESS_TOKEN,
  FacilityID,
  ORGANISATONID,
} from "../../../constants/defaultKeys";
import { getCookie } from "../../../helpers/localStorage";
import { UserContext } from "../../../context/UserContext";

import GalleryPanel from "./GalleryPanel";
import ChatPanel from "./ChatPanel";
import { buildPartnersDropdown } from "./PartnersDropdown"; // optional — see note below
import "./Chat.css";
import partnerImg from "../../../assets/fileicons/Partners.svg";
import internalImg from "../../../assets/fileicons/internal.svg";

// ─── constants ───────────────────────────────────────────────────────────────

// Server close codes: 4401 = unauthenticated, 4403 = no access to this chat.
// Reconnecting can never succeed for these, so we stop and tell the user.
const FATAL_CLOSE_REASONS = {
  4401: "Your session has expired. Please log in again.",
  4403: "You don't have access to this chat.",
};
const RECONNECT_ATTEMPTS = 10;
const MAX_RECONNECT_DELAY_MS = 30000;
const MAX_MESSAGE_LENGTH = 5000;
const HISTORY_PAGE_SIZE = 10;
const MARK_READ_DEBOUNCE_MS = 1500;
const MENTION_SEARCH_DEBOUNCE_MS = 300;
const NEAR_BOTTOM_PX = 150;

// ─── helpers ─────────────────────────────────────────────────────────────────

const stringToColor = (str) => {
  if (!str) return "#667085";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 65%, 55%)`;
};

const getInitial = (name) => name?.trim()?.charAt(0)?.toUpperCase() || "?";

export const convertToFormData = (res, file) => {
  const formData = new FormData();
  for (const key in res?.data?.fields)
    formData.append(key, res.data.fields[key]);
  formData.append("file", file);
  return formData;
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const getSenderId = (msg) =>
  msg?.sender_id ?? msg?.sent_by?.id ?? msg?.sender?.id ?? msg?.user_id;

const toTimestamp = (value) => {
  if (value == null) return null;
  const t = dayjs(value).valueOf();
  return Number.isFinite(t) ? t : null;
};

// Sort by created_at, then by id.
const compareMessages = (a, b) => {
  const ta = toTimestamp(a?.created_at);
  const tb = toTimestamp(b?.created_at);
  if (ta != null && tb != null && ta !== tb) return ta - tb;
  const ia = Number(a?.id);
  const ib = Number(b?.id);
  if (Number.isFinite(ia) && Number.isFinite(ib)) return ia - ib;
  return 0;
};

// Merge messages by id (later entries win) and keep them ordered.
const mergeMessages = (prev, incoming) => {
  const byId = new Map();
  const withoutId = [];
  [...(prev || []), ...(incoming || [])].forEach((msg) => {
    if (msg?.id == null) {
      withoutId.push(msg);
      return;
    }
    const key = String(msg.id);
    byId.set(key, { ...(byId.get(key) || {}), ...msg });
  });
  return [...byId.values(), ...withoutId].sort(compareMessages);
};

const dedupeById = (list) =>
  (list || []).filter(
    (msg, i, self) =>
      msg?.id == null || i === self.findIndex((m) => sameId(m?.id, msg?.id))
  );

const isGalleryAttachment = (msg) =>
  msg?.attachment?.type?.startsWith("image/") ||
  msg?.attachment?.type === "application/pdf";

const getMentionName = (user) =>
  `${user?.first_name ?? ""}${user?.last_name ? " " + user.last_name : ""}`;

const getTextAreaElement = (ref) =>
  ref?.current?.resizableTextArea?.textArea ?? null;

// ─── tab styles ──────────────────────────────────────────────────────────────

const tabStyle = (active, colorPrimary) => ({
  padding: "8px 24px",
  cursor: "pointer",
  fontWeight: active ? 700 : 500,
  fontSize: 14,
  color: active ? colorPrimary : "#6B7280",
  borderBottom: active ? `2px solid ${colorPrimary}` : "2px solid transparent",
  transition: "all 0.2s",
  userSelect: "none",
  whiteSpace: "nowrap",
});

// ─── component ───────────────────────────────────────────────────────────────

const ChatContainer = ({
  patientData,
  mode = "partner",
  isMobile = false,
  onActivity,
}) => {
  const isInternal = mode === "internal";

  const { token: antdToken } = theme.useToken();
  const colorPrimary = antdToken.colorPrimary;

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const patientId = params.get("patient");

  const { userDetails } = useContext(UserContext);
  const currentUserId = userDetails?.id;

  const token = getCookie(ACCESS_TOKEN);
  const organization = getCookie(ORGANISATONID);
  const facility = getCookie(FacilityID);

  const sessionId = patientData?.admission?.ip_id;
  const partners = patientData?.admission?.partners || [];
  const hasPartners = partners.length > 0;

  // ─── tabs ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("chat"); // "chat" | "gallery"

  // ─── messages ────────────────────────────────────────────────────────────
  // This component is keyed by `${mode}-${sessionId}` in ChatTabs, so all state
  // below belongs to exactly one chat session.
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [editMsg, setEditMsg] = useState(null);
  const [editingMessage, setEditingMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const oldestHistoryIdRef = useRef(null);
  const initialLoadedRef = useRef(false);
  const pendingScrollRef = useRef(null);
  const isSavingEditRef = useRef(false);
  const mountedRef = useRef(true);

  // ─── gallery ─────────────────────────────────────────────────────────────
  const [galleryImages, setGalleryImages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(4);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);
  const galleryRequestIdRef = useRef(0);

  // ─── misc ─────────────────────────────────────────────────────────────────
  const [activeUsers, setActiveUsers] = useState([]);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const inputRef = useRef(null);
  const markReadTimerRef = useRef(null);
  const scrollTimerRef = useRef(null);

  // ─── mention (internal only) ──────────────────────────────────────────────
  const [mentionResults, setMentionResults] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const mentionRangeRef = useRef(null);
  const mentionTimerRef = useRef(null);
  const mentionRequestIdRef = useRef(0);

  // ─── connection state ─────────────────────────────────────────────────────
  const [fatalReason, setFatalReason] = useState(null);
  const [reconnectGaveUp, setReconnectGaveUp] = useState(false);
  const [wasDisconnected, setWasDisconnected] = useState(false);
  const wasDisconnectedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current);
    };
  }, []);

  // ─── scroll helpers ───────────────────────────────────────────────────────

  const isNearBottom = () => {
    const el = messagesEndRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  const scrollToBottom = useCallback(() => {
    const apply = () => {
      const el = messagesEndRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    apply();
    requestAnimationFrame(apply);
    // images may still be loading; settle once more shortly after
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(apply, 300);
  }, []);

  // Apply the scroll requested by whatever last changed `messages`:
  // "bottom" (first load / own send / user already near bottom) or
  // "restore" (older page prepended → keep the visible message in place).
  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    const container = messagesEndRef.current;
    if (!pending || !container) return;
    pendingScrollRef.current = null;

    if (pending.type === "bottom") {
      scrollToBottom();
      return;
    }
    if (pending.type === "restore") {
      const anchor =
        pending.anchorId != null
          ? container.querySelector(`[id="${CSS.escape(pending.anchorId)}"]`)
          : null;
      if (anchor && pending.anchorOffset != null) {
        const offset =
          anchor.getBoundingClientRect().top -
          container.getBoundingClientRect().top;
        container.scrollTop += offset - pending.anchorOffset;
      } else {
        container.scrollTop =
          container.scrollHeight - pending.prevHeight + pending.prevTop;
      }
    }
  }, [messages, isLoading, scrollToBottom]);

  // Returning to the chat tab remounts the list; show the latest messages.
  useLayoutEffect(() => {
    if (activeTab === "chat" && initialLoadedRef.current) scrollToBottom();
  }, [activeTab, scrollToBottom]);

  const captureScrollAnchor = () => {
    const container = messagesEndRef.current;
    if (!container) return null;
    const containerTop = container.getBoundingClientRect().top;
    const items = container.querySelectorAll(".chat-message-item[id]");
    let anchorId = null;
    let anchorOffset = null;
    for (const item of items) {
      const offset = item.getBoundingClientRect().top - containerTop;
      if (offset >= 0) {
        anchorId = item.id;
        anchorOffset = offset;
        break;
      }
    }
    return {
      type: "restore",
      prevHeight: container.scrollHeight,
      prevTop: container.scrollTop,
      anchorId,
      anchorOffset,
    };
  };

  // ─── mark as read ─────────────────────────────────────────────────────────

  const markRead = useCallback(() => {
    if (!sessionId) return;
    const api = isInternal ? markInternalChatAsReadApi : markChatAsReadApi;
    api(sessionId).catch(console.error);
  }, [sessionId, isInternal]);

  const scheduleMarkRead = () => {
    if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    markReadTimerRef.current = setTimeout(() => {
      markReadTimerRef.current = null;
      markRead();
    }, MARK_READ_DEBOUNCE_MS);
  };

  // ─── fetch ────────────────────────────────────────────────────────────────

  const historyApi = isInternal ? getInternalChatHistoryApi : getChatHistoryApi;

  const fetchChatHistory = useCallback(async () => {
    if (!sessionId || isLoadingRef.current || !hasMoreRef.current) return;
    const isFirstPage = oldestHistoryIdRef.current == null;

    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const response = await historyApi(sessionId, {
        limit: HISTORY_PAGE_SIZE,
        last_message_id: oldestHistoryIdRef.current ?? undefined,
      });
      if (!mountedRef.current) return;
      const page = response?.data?.data || [];

      const ids = page.map((m) => Number(m?.id)).filter(Number.isFinite);
      if (ids.length > 0) {
        const oldest = Math.min(...ids);
        if (
          oldestHistoryIdRef.current != null &&
          oldest >= oldestHistoryIdRef.current
        ) {
          // server returned nothing older; stop paginating
          hasMoreRef.current = false;
        }
        oldestHistoryIdRef.current =
          oldestHistoryIdRef.current == null
            ? oldest
            : Math.min(oldestHistoryIdRef.current, oldest);
      }
      if (ids.length === 0 || page.length < HISTORY_PAGE_SIZE) {
        hasMoreRef.current = false;
      }

      if (page.length > 0) {
        pendingScrollRef.current = isFirstPage
          ? { type: "bottom" }
          : captureScrollAnchor();
        setMessages((prev) => mergeMessages(prev, page));
      }

      if (isFirstPage) {
        initialLoadedRef.current = true;
        markRead();
      }
    } catch (error) {
      if (mountedRef.current) {
        message.error(
          error?.response?.data?.message ?? "Failed to load chat history"
        );
      }
    } finally {
      isLoadingRef.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  }, [sessionId, historyApi, markRead]);

  // After a reconnect, pick up anything sent while we were offline.
  const refreshLatestMessages = useCallback(async () => {
    if (!sessionId || !initialLoadedRef.current) return;
    try {
      const response = await historyApi(sessionId, { limit: HISTORY_PAGE_SIZE });
      if (!mountedRef.current) return;
      const page = response?.data?.data || [];
      if (page.length === 0) return;
      if (isNearBottom()) pendingScrollRef.current = { type: "bottom" };
      setMessages((prev) => mergeMessages(prev, page));
    } catch (error) {
      console.error("Failed to refresh chat messages:", error);
    }
  }, [sessionId, historyApi]);

  const fetchGalleryImages = useCallback(async () => {
    if (!patientId) return;
    const requestId = ++galleryRequestIdRef.current;
    setIsGalleryLoading(true);
    try {
      const galleryMode = isInternal ? "internal" : "partner";
      const response = await getChatGalleryApi(patientId, galleryMode);
      if (!mountedRef.current || requestId !== galleryRequestIdRef.current)
        return;
      const list = response?.data?.data || [];
      // keep live attachments that arrived while the request was in flight
      setGalleryImages((prev) => dedupeById([...prev, ...list]));
    } catch (err) {
      if (
        mountedRef.current &&
        requestId === galleryRequestIdRef.current &&
        !isInternal &&
        err?.response?.status === 404
      ) {
        setGalleryImages([]);
      }
    } finally {
      if (mountedRef.current && requestId === galleryRequestIdRef.current) {
        setIsGalleryLoading(false);
      }
    }
  }, [patientId, isInternal]);

  useEffect(() => {
    if (sessionId) fetchChatHistory();
    // fetchChatHistory is stable for a given session; run once per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    fetchGalleryImages();
  }, [fetchGalleryImages]);

  useEffect(() => {
    requestAnimationFrame(() =>
      textareaRef.current?.focus?.({ cursor: "end" })
    );
  }, [editingMessage]);

  const handleScroll = useCallback(
    (e) => {
      if (!initialLoadedRef.current) return;
      if (e.target.scrollTop < 100) fetchChatHistory();
    },
    [fetchChatHistory]
  );

  // ─── edit / delete local state ────────────────────────────────────────────

  const cancelEdit = () => {
    setEditMsg(null);
    setEditingMessage("");
  };

  const applyMessageEdit = (msgId, text) => {
    setMessages((prev) =>
      prev.map((m) =>
        sameId(m?.id, msgId) ? { ...m, message: text, is_edited: true } : m
      )
    );
  };

  const removeMessage = (msgId) => {
    setMessages((prev) => prev.filter((m) => !sameId(m?.id, msgId)));
    setGalleryImages((prev) => prev.filter((m) => !sameId(m?.id, msgId)));
    if (sameId(editMsg, msgId)) cancelEdit();
  };

  // ─── websocket ────────────────────────────────────────────────────────────

  const handleSocketEvent = (data) => {
    switch (data?.message_type) {
      case "message":
      case "attachment": {
        const isOwn = sameId(getSenderId(data), currentUserId);
        if (isOwn || isNearBottom()) {
          pendingScrollRef.current = { type: "bottom" };
        }
        setMessages((prev) => mergeMessages(prev, [data]));
        if (data.message_type === "attachment" && isGalleryAttachment(data)) {
          setGalleryImages((prev) => dedupeById([data, ...prev]));
        }
        if (!isOwn) scheduleMarkRead();
        onActivity?.();
        break;
      }
      case "edit_message":
        applyMessageEdit(data.id, data.message);
        onActivity?.();
        break;
      case "delete_message":
        removeMessage(data.id);
        onActivity?.();
        break;
      case "active_users":
        setActiveUsers(data?.users || []);
        break;
      case "error":
        message.error(data?.message || "Something went wrong");
        break;
      default:
        break;
    }
  };

  const wsUrl = useMemo(() => {
    // No socket until we know which admission this is (never /ws/chat/undefined/),
    // and none after the server rejected us with 4401/4403.
    if (!sessionId || fatalReason) return null;
    const base = process.env.REACT_APP_WS_BASE_URL;
    return isInternal
      ? `${base}/ws/interdepartment-chat/${sessionId}_internal/`
      : `${base}/ws/chat/${sessionId}/`;
  }, [sessionId, isInternal, fatalReason]);

  const { sendJsonMessage, readyState, getWebSocket } = useWebSocket(wsUrl, {
    queryParams: { token, organization, facility },
    shouldReconnect: (event) => !FATAL_CLOSE_REASONS[event?.code],
    reconnectAttempts: RECONNECT_ATTEMPTS,
    reconnectInterval: (attempt) =>
      Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS),
    onOpen: () => {
      setReconnectGaveUp(false);
      if (wasDisconnectedRef.current) {
        wasDisconnectedRef.current = false;
        setWasDisconnected(false);
        refreshLatestMessages();
      }
    },
    onClose: (event) => {
      const reason = FATAL_CLOSE_REASONS[event?.code];
      if (reason) {
        setFatalReason(reason);
        return;
      }
      wasDisconnectedRef.current = true;
      setWasDisconnected(true);
    },
    onReconnectStop: () => setReconnectGaveUp(true),
    onMessage: (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (err) {
        console.error("Invalid chat socket payload:", err);
        return;
      }
      handleSocketEvent(data);
    },
    // events are handled in onMessage; skip lastMessage state updates
    filter: () => false,
  });

  const isConnected = readyState === ReadyState.OPEN;
  const connectionStatus = !sessionId
    ? null
    : fatalReason ||
      (isConnected
        ? null
        : reconnectGaveUp
        ? "Disconnected. Please refresh the page to reconnect."
        : wasDisconnected
        ? "Reconnecting…"
        : null);

  // Only send on an open socket and never queue: queued messages could be
  // flushed later into a different connection.
  const sendOverSocket = (payload) => {
    if (getWebSocket()?.readyState !== WebSocket.OPEN) return false;
    sendJsonMessage(payload, false);
    return true;
  };

  // ─── send ─────────────────────────────────────────────────────────────────

  const clearComposer = (sentText) => {
    setNewMessage((curr) => (curr.trim() === sentText ? "" : curr));
    setTaggedUsers([]);
    setShowMentionDropdown(false);
    setMentionResults([]);
    mentionRangeRef.current = null;
  };

  const handleSaveEdit = async () => {
    const text = editingMessage.trim();
    if (!text || isSavingEditRef.current) return;
    if (text.length > MAX_MESSAGE_LENGTH) {
      message.error(`Message is too long (max ${MAX_MESSAGE_LENGTH} characters).`);
      return;
    }
    const msgId = editMsg;
    isSavingEditRef.current = true;
    try {
      const editApi = isInternal ? editInternalChatApi : editChatApi;
      await editApi(sessionId, msgId, { message: text });
      if (!mountedRef.current) return;
      applyMessageEdit(msgId, text);
      cancelEdit();
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to edit message");
    } finally {
      isSavingEditRef.current = false;
    }
  };

  const handleSendMessage = async () => {
    if (editMsg) {
      await handleSaveEdit();
      return;
    }
    if (!isInternal && !hasPartners) {
      message.warning("No partners available. You cannot send messages.");
      return;
    }
    if (isUploading) return;

    const text = newMessage.trim();
    if (!text && !pendingFile) return;
    if (text.length > MAX_MESSAGE_LENGTH) {
      message.error(`Message is too long (max ${MAX_MESSAGE_LENGTH} characters).`);
      return;
    }
    if (getWebSocket()?.readyState !== WebSocket.OPEN) {
      message.error("Chat is not connected. Please wait and try again.");
      return;
    }

    // only tag users whose mention text is still in the message
    const taggedIds = isInternal
      ? taggedUsers
          .filter((u) => text.includes(getMentionName(u)))
          .map((u) => u.user__id)
      : [];
    const textPayload = text
      ? {
          msg_type: "message",
          message: text,
          ...(taggedIds.length > 0 && { tagged_user_ids: taggedIds }),
        }
      : null;

    if (pendingFile) {
      await uploadAndSendAttachment(pendingFile, textPayload);
      return;
    }

    if (!sendOverSocket(textPayload)) {
      message.error("Chat is not connected. Your message was not sent.");
      return;
    }
    clearComposer(text);
  };

  // ─── edit / delete ────────────────────────────────────────────────────────

  const handleEditAction = (msgId, msgText) => {
    setEditMsg(msgId);
    setEditingMessage(msgText || "");
  };

  const handleDeleteAction = async (msgId) => {
    try {
      const deleteApi = isInternal ? deleteInternalChatApi : deleteChatApi;
      await deleteApi(sessionId, msgId);
      if (mountedRef.current) removeMessage(msgId);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to delete message"
      );
    }
  };

  // ─── file ─────────────────────────────────────────────────────────────────

  const handleFileClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (file.size > 30 * 1024 * 1024) {
      message.error("File size exceeds 30MB.");
      return;
    }
    if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) {
      message.error("Only PNG, JPG, or PDF files allowed!");
      return;
    }
    setPendingFile(file);
  };

  const uploadAttachmentToS3 = async (file) => {
    const res = await getUploadUrlApi(
      `path_key=${encodeURIComponent(
        file.name
      )}&admission_type=IP&admission_id=${encodeURIComponent(sessionId)}`
    );
    const key = res?.data?.fields?.key;
    if (!res?.data?.url || !key) throw new Error("Upload URL not available");
    const formData = convertToFormData(res, file);
    await axios.post(res.data.url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { key, type: file.type };
  };

  const uploadAndSendAttachment = async (file, textPayload) => {
    setIsUploading(true);
    try {
      const uploaded = await uploadAttachmentToS3(file);
      if (!mountedRef.current) return;
      const sent = sendOverSocket({
        msg_type: "attachment",
        attachment: { path: uploaded.key, type: uploaded.type },
      });
      if (!sent) {
        message.error("Chat is not connected. The file was not sent.");
        return;
      }
      setPendingFile(null);
      message.success("File uploaded successfully");
      if (textPayload) {
        if (sendOverSocket(textPayload)) {
          clearComposer(textPayload.message);
        } else {
          message.error("Chat is not connected. Your message was not sent.");
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        message.error(err?.response?.data?.message || "Upload failed");
      }
    } finally {
      if (mountedRef.current) setIsUploading(false);
    }
  };

  // ─── mention (internal only) ──────────────────────────────────────────────

  const resetMentionSearch = () => {
    mentionRequestIdRef.current += 1;
    if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current);
    mentionRangeRef.current = null;
    setShowMentionDropdown(false);
    setMentionResults([]);
    setMentionLoading(false);
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (!isInternal) return;

    // drop tags whose mention text has been removed from the message
    if (taggedUsers.length > 0) {
      setTaggedUsers((prev) =>
        prev.filter((u) => value.includes(getMentionName(u)))
      );
    }

    const cursorPos = e.target.selectionStart ?? value.length;
    const mentionMatch = value.slice(0, cursorPos).match(/@(\w*)$/);

    if (!mentionMatch) {
      resetMentionSearch();
      return;
    }

    const query = mentionMatch[1];
    mentionRangeRef.current = {
      start: cursorPos - mentionMatch[0].length,
      end: cursorPos,
    };
    setShowMentionDropdown(true);

    const requestId = ++mentionRequestIdRef.current;
    if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current);
    if (!query) {
      setMentionResults([]);
      setMentionLoading(false);
      return;
    }

    setMentionLoading(true);
    mentionTimerRef.current = setTimeout(async () => {
      try {
        const res = await getPractitionerSearchApi(
          `query=${encodeURIComponent(query)}&facility=${encodeURIComponent(
            facility ?? ""
          )}&role=practitioners`
        );
        if (!mountedRef.current || requestId !== mentionRequestIdRef.current)
          return;
        setMentionResults(res?.data || []);
      } catch (_) {
        if (mountedRef.current && requestId === mentionRequestIdRef.current)
          setMentionResults([]);
      } finally {
        if (mountedRef.current && requestId === mentionRequestIdRef.current)
          setMentionLoading(false);
      }
    }, MENTION_SEARCH_DEBOUNCE_MS);
  };

  const handleMentionSelect = (user) => {
    const mentionName = getMentionName(user);
    const range = mentionRangeRef.current;
    const current = newMessage || "";
    let next;
    let caret;
    if (
      range &&
      /^@\w*$/.test(current.slice(range.start, range.end))
    ) {
      next =
        current.slice(0, range.start) +
        `${mentionName} ` +
        current.slice(range.end);
      caret = range.start + mentionName.length + 1;
    } else {
      next = current.replace(/@(\w*)$/, `${mentionName} `);
      caret = next.length;
    }
    setNewMessage(next);
    setTaggedUsers((prev) =>
      prev.find((u) => u.user__id === user.user__id) ? prev : [...prev, user]
    );
    resetMentionSearch();

    requestAnimationFrame(() => {
      const el = getTextAreaElement(inputRef);
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const handleRemoveTag = (userId) => {
    const toRemove = taggedUsers.find((u) => u.user__id === userId);
    if (toRemove) {
      const name = getMentionName(toRemove);
      // plain string replacement — names must not be treated as regex
      setNewMessage((msg) =>
        (msg || "")
          .split(`@${name}`)
          .join("")
          .split(name)
          .join("")
          .replace(/[ \t]{2,}/g, " ")
      );
    }
    setTaggedUsers((prev) => prev.filter((u) => u.user__id !== userId));
  };

  // ─── partners header ──────────────────────────────────────────────────────

  const partnersHeader =
    !isInternal && hasPartners ? (
      <Dropdown
        overlay={buildPartnersDropdown(partners, activeUsers)}
        trigger={["click"]}
        placement="bottomRight"
      >
        <div style={{ cursor: "pointer" }}>
          <Avatar.Group maxCount={5} size={28}>
            {partners.map((item, index) => (
              <Avatar
                key={index}
                size={28}
                style={{
                  backgroundColor: stringToColor(item.name),
                  fontWeight: 900,
                  fontSize: 14,
                }}
              >
                {getInitial(item.name)}
              </Avatar>
            ))}
          </Avatar.Group>
        </div>
      </Dropdown>
    ) : null;

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <div className="chat-container-shell">
      {/* Tab bar */}
      <div
        className={`chat-header-bar ${isMobile ? "chat-header-bar--mobile" : ""}`}
      >
        {isMobile ? (
          <>
            <div className="chat-header-tabs chat-header-tabs--mobile">
              {["chat", "gallery"].map((tab) => (
                <div
                  key={tab}
                  className="chat-header-tab-item"
                  style={tabStyle(activeTab === tab, colorPrimary)}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </div>
              ))}
            </div>
            <div className="chat-header-actions">{partnersHeader}</div>
          </>
        ) : (
          <>
            <div className="chat-header-main">
              <div
                style={{
                  display: "flex",
                  backgroundColor: colorPrimary,
                  borderRadius: "100%",
                  border: "1px solid white",
                  width: "40px",
                  height: "40px",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Image
                  src={mode === "internal" ? internalImg : partnerImg}
                  alt={mode}
                  preview={false}
                />
              </div>

              <span className="chat-header-title">
                {mode === "internal" ? "Internal Chat" : "Partners Chat"}
              </span>

              {["chat", "gallery"].map((tab) => (
                <div
                  key={tab}
                  style={tabStyle(activeTab === tab, colorPrimary)}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </div>
              ))}
            </div>

            <div className="chat-header-actions">{partnersHeader}</div>
          </>
        )}
      </div>
      {/* Tab content */}
      <div className="chat-tab-content">
        {activeTab === "gallery" ? (
          <div className="chat-gallery-scroll">
            <GalleryPanel
              galleryImages={galleryImages}
              isGalleryLoading={isGalleryLoading}
              visibleCount={visibleCount}
              onLoadMore={() => setVisibleCount((v) => v + 4)}
            />
          </div>
        ) : (
          <ChatPanel
            messages={messages}
            isLoading={isLoading}
            sessionId={sessionId}
            colorPrimary={colorPrimary}
            userId={currentUserId}
            activeUsers={activeUsers}
            editMsg={editMsg}
            editingMessage={editingMessage}
            setEditingMessage={setEditingMessage}
            onCancelEdit={() => {
              cancelEdit();
              setNewMessage("");
            }}
            newMessage={newMessage}
            onMessageChange={handleMessageChange}
            onSend={handleSendMessage}
            inputRef={inputRef}
            onFileClick={handleFileClick}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            pendingFile={pendingFile}
            isUploading={isUploading}
            connectionStatus={connectionStatus}
            onClearPendingFile={() => {
              if (isUploading) return;
              setPendingFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            onRightClick={(e, msg) => {
              /* handled inside ChatMessage */
            }}
            onEditAction={handleEditAction}
            onDeleteAction={handleDeleteAction}
            showMentionDropdown={isInternal ? showMentionDropdown : undefined}
            mentionResults={isInternal ? mentionResults : undefined}
            mentionLoading={isInternal ? mentionLoading : undefined}
            onMentionSelect={isInternal ? handleMentionSelect : undefined}
            taggedUsers={isInternal ? taggedUsers : undefined}
            onRemoveTag={isInternal ? handleRemoveTag : undefined}
            messagesEndRef={messagesEndRef}
            onScroll={handleScroll}
            textareaRef={textareaRef}
            headerRight={null}
            sendDisabled={
              (!isInternal && !hasPartners) || !isConnected || isUploading
            }
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
};

export default ChatContainer;
